import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData, postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment, Chip, CircularProgress, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ProductSlabModal from './ProductSlabModal';
import Pagination from '../../common/Pagination';

function ProductRelationIndex() {
  // Session validation
  useSessionValidation();

  const [data, setData] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, userId, logout, token } = useAuth();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  // Expanded rows
  const [expandedProductIds, setExpandedProductIds] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;

    setLoading(true);
    fetchtableData().finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  const fetchtableData = async () => {
    try {
      // Fetch all items first
      const itemsResponse = await fetchData('ItemApi', `/${companyId}`, token);
      const itemsData = itemsResponse.response_data || itemsResponse || [];
      setItems(itemsData);

      // Fetch categories
      const categoriesResponse = await fetchData('CategoryApi', `/${companyId}`, token);
      const categoriesData = categoriesResponse.response_data || categoriesResponse || [];
      setCategories(categoriesData);

      // Fetch product relations
      const response = await fetchData('ItemProductRelationApi', `/${companyId}`, token);
      console.log('Product Relation data response:', response);

      if (response && response.responseCode === '1111' && response.response_data) {
        const relations = response.response_data;

        // Group relations by ItemId (parent product)
        const groupedData = relations.reduce((acc, relation) => {
          const existingProduct = acc.find(p => p.ItemId === relation.ItemId);
          if (existingProduct) {
            existingProduct.relations.push(relation);
          } else {
            acc.push({
              ItemId: relation.ItemId,
              ItemMasterName: relation.ItemMasterName,
              relations: [relation]
            });
          }
          return acc;
        }, []);

        setData(groupedData);
        console.log('Product Relation data loaded successfully');
      } else {
        console.log('Product Relation API failed or returned no data');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Product Relation Details:', error);
      setData([]);
    }
  };

  const handleDelete = async () => {
    deleteData('ItemProductRelationApi', `${deleteItemId}`, token)
      .then(() => {
        fetchtableData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  // Modal handlers
  const handleOpenModal = async (productId) => {
    setSelectedProductId(productId);

    // If productId is provided, fetch the existing relations from API
    if (productId) {
      setLoading(true);
      try {
        const response = await fetchData('ItemProductRelationApi', `/SlabDetails/${productId}`, token);
        const responseData = response.response_data || response;

        // Extract ItemProductRelation array from response
        const relations = responseData?.ItemProductRelation || [];

        console.log('Fetched product slab relations:', relations);

        // Update the data state with fresh API data
        setData(prevData => {
          const updatedData = prevData.filter(item => item.ItemId !== productId);
          if (relations.length > 0) {
            updatedData.push({
              ItemId: productId,
              ItemMasterName: relations[0]?.ItemMasterName || '',
              relations: relations
            });
          }
          return updatedData;
        });
      } catch (error) {
        console.error('Error fetching product slab details:', error);
      } finally {
        setLoading(false);
      }
    }

    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProductId(null);
  };

  // Get existing relations for a product
  const getProductRelations = (productId) => {
    if (!productId) return [];
    const productData = data.find(item => item.ItemId === productId);
    return productData ? productData.relations : [];
  };

  const handleSaveProductSlab = async (selectedProductId, productRelations) => {
    try {
      setLoading(true);

      console.log('Saving Product Slab...');
      console.log('Product ID:', selectedProductId);
      console.log('Relations to save:', productRelations);

      const product = items.find(item => item.Id === selectedProductId);

      // Check if this is an edit (has existing relations with Id) or new
      const isEdit = productRelations.some(rel => rel.Id);

      const itemProductRelations = productRelations.map(rel => {
        const childItem = items.find(item => item.Id === rel.ProductItemId);
        const relationData = {
          ItemId: parseInt(selectedProductId),
          ProductItemId: parseInt(rel.ProductItemId),
          Quantity: parseFloat(rel.Quantity) || 0,
          Rate: parseFloat(rel.Rate) || 0,
          ItemMasterName: product?.Name || '',
          ProductChildName: childItem?.Name || '',
          IsActive: true
        };

        if (rel.Id) {
          // Existing relation - UPDATE
          relationData.Id = rel.Id;
          relationData.ModifiedBy = userId;
          relationData.ModifiedDate = new Date().toISOString();
        } else {
          // New relation - INSERT
          relationData.CreatedBy = userId;
          relationData.CreatedDate = new Date().toISOString();
        }

        return relationData;
      });

      if (isEdit) {
        // EDIT mode - Send ItemVM structure
        const ItemVM = {
          Item: {
            ...product,
            ModifiedBy: userId,
            CreatedBy: product.CreatedBy || userId
          },
          ItemProductRelation: itemProductRelations,
          MainMasterItemID: parseInt(selectedProductId)
        };
        console.log('Sending to API (EDIT - ItemVM format):', ItemVM);
        await updateData('ItemProductRelationApi', selectedProductId, ItemVM, token);
      } else {
        // ADD mode - Send ItemVM structure
        const ItemVM = {
          Item: {
            ...product,
            ModifiedBy: product.ModifiedBy,
            CreatedBy: userId
          },
          ItemProductRelation: itemProductRelations,
          MainMasterItemID: parseInt(selectedProductId)
        };
        console.log('Sending to API (ADD - ItemVM format):', ItemVM);
        await postData('ItemProductRelationApi', '', ItemVM, token);
      }

      await fetchtableData();
      setDialogMessage('Product slab saved successfully!');
      setSuccessDialogOpen(true);
      handleCloseModal();
      
      // Auto-close success dialog after 2 seconds and reload
      setTimeout(() => {
        setSuccessDialogOpen(false);
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error saving product slab:', error);
      setDialogMessage('Failed to save product slab. Please try again.');
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Toggle expand/collapse
  const toggleExpand = (productId) => {
    setExpandedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  // Filter data based on search term
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.ItemMasterName?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Define table columns
  const columns = [
    { field: 'SlNo', header: 'Sl.No', align: 'center' },
    { field: 'ItemMasterName', header: 'Product Name', align: 'left' },
    { field: 'TotalItems', header: 'Total Items', align: 'center' },
  ];

  // Define action buttons for each row
  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      <Tooltip title="Manage Product Slab" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal(item.ItemId)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            background: 'white',
            color: '#667eea',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          ⚙️
        </Button>
      </Tooltip>

      <Tooltip title="Expand/Collapse" arrow>
        <Button
          size="small"
          onClick={() => toggleExpand(item.ItemId)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          {expandedProductIds.includes(item.ItemId) ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
        </Button>
      </Tooltip>

      <Tooltip title="Delete Product Slab" arrow>
        <Button
          size="small"
          onClick={() => openConfirmDialog(item.ItemId)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #f9f7f7ff 0%, rgba(244, 237, 237, 1) 100%)',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(239, 68, 68, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          🗑️
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideDown {
            from { 
              opacity: 0; 
              transform: translateY(-10px);
              max-height: 0;
            }
            to { 
              opacity: 1; 
              transform: translateY(0);
              max-height: 500px;
            }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        {loading && (
          <Box
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 9999,
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              style={{
                color: '#667eea',
              }}
            />
          </Box>
        )}

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section with Title */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Product Relations</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Search Bar and Add Slab Button in Same Row */}
          <div style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by product name..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#667eea', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '13px',
                  height: '29px',
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            />
            <Tooltip title="Add product slab" arrow>
              <Button
                variant="contained"
                onClick={() => handleOpenModal(null)}
                className="action-button"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                  border: 'none',
                  minHeight: '29px',
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ fontSize: '12px' }}>+</span>
                Add Relation
              </Button>
            </Tooltip>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="product slab table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #612261ff 0%, #764ba2 100%)',
                    height: '27px'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '4px 12px',
                        textAlign: column.align || 'left',
                        height: '27px'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '4px 12px',
                      textAlign: 'right',
                      height: '27px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => {
                    const isExpanded = expandedProductIds.includes(item.ItemId);
                    return (
                      <>
                        <TableRow key={item.ItemId} className="table-row" style={{
                          background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          borderBottom: '2px solid #f0f0f0'
                        }}>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>
                            {startIndex + index + 1}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {item.ItemMasterName}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>
                            <Chip
                              label={item.relations.length}
                              size="small"
                              color="primary"
                              style={{ fontSize: '11px', fontWeight: '600' }}
                            />
                          </TableCell>
                          <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                            {renderActions(item)}
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row - Child Items */}
                        {isExpanded && (
                          <TableRow style={{
                            animation: 'slideDown 0.3s ease-out'
                          }}>
                            <TableCell colSpan={4} style={{ padding: '0', background: 'linear-gradient(to bottom, #f8f9fa, #ffffff)' }}>
                              <div style={{
                                padding: '16px',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                                borderLeft: '3px solid #667eea'
                              }}>
                                <div style={{
                                  marginBottom: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <span style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#667eea',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Child Items
                                  </span>
                                  <div style={{
                                    flex: 1,
                                    height: '1px',
                                    background: 'linear-gradient(to right, #667eea, transparent)'
                                  }}></div>
                                </div>
                                <Table size="small" style={{
                                  background: 'white',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}>
                                  <TableHead>
                                    <TableRow style={{
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      height: '24px'
                                    }}>
                                      <TableCell style={{
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: 'white',
                                        padding: '4px 12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                      }}>
                                        Child Item
                                      </TableCell>
                                      <TableCell style={{
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: 'white',
                                        textAlign: 'right',
                                        padding: '4px 12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                      }}>
                                        Quantity
                                      </TableCell>
                                      <TableCell style={{
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: 'white',
                                        textAlign: 'right',
                                        padding: '4px 12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                      }}>
                                        Rate
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {item.relations.map((relation, idx) => (
                                      <TableRow
                                        key={idx}
                                        style={{
                                          background: idx % 2 === 0 ? 'white' : '#f8f9fa',
                                          transition: 'background 0.2s ease',
                                          cursor: 'default'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#f8f9fa'}
                                      >
                                        <TableCell style={{
                                          fontSize: '11px',
                                          padding: '8px 12px',
                                          color: '#2d3748',
                                          fontWeight: '500'
                                        }}>
                                          {relation.ProductChildName}
                                        </TableCell>
                                        <TableCell style={{
                                          fontSize: '11px',
                                          padding: '8px 12px',
                                          color: '#2d3748',
                                          textAlign: 'right',
                                          fontWeight: '600'
                                        }}>
                                          <span style={{
                                            background: '#e3f2fd',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            color: '#1976d2'
                                          }}>
                                            {relation.Quantity}
                                          </span>
                                        </TableCell>
                                        <TableCell style={{
                                          fontSize: '11px',
                                          padding: '8px 12px',
                                          color: '#2d3748',
                                          textAlign: 'right',
                                          fontWeight: '600'
                                        }}>
                                          <span style={{ fontSize: '10px', color: '#666' }}>AED </span>{relation.Rate}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this product slab record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Product Slab Modal */}
        <ProductSlabModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveProductSlab}
          products={items}
          items={items}
          categories={categories}
          loading={loading}
          editProductId={selectedProductId}
          editRelations={getProductRelations(selectedProductId)}
          existingProductIds={data.map(item => item.ItemId)}
        />

        {/* Success Dialog */}
        <Dialog
          open={successDialogOpen}
          onClose={() => {}}
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px',
              minWidth: '320px'
            }
          }}
        >
          <DialogTitle style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#10b981',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '24px' }}>✅</span>
            Success
          </DialogTitle>
          <DialogContent>
            <DialogContentText style={{
              fontSize: '13px',
              color: '#666'
            }}>
              {dialogMessage}
            </DialogContentText>
          </DialogContent>
        </Dialog>

        {/* Error Dialog */}
        <Dialog
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px',
              minWidth: '320px'
            }
          }}
        >
          <DialogTitle style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '24px' }}>❌</span>
            Error
          </DialogTitle>
          <DialogContent>
            <DialogContentText style={{
              fontSize: '13px',
              color: '#666'
            }}>
              {dialogMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px' }}>
            <Button
              onClick={() => setErrorDialogOpen(false)}
              style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '6px 16px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
                border: 'none'
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

      </div>
    </Fade >
  );
}

export default ProductRelationIndex;
